import React, { useEffect, useState } from "react";
import { facilityAdmin } from "../../api";
import { PageHeader, Btn, LoadingSpinner, StatCard } from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";
import BRAND from "../../brand";
import DailyOverview from "./DailyOverview";
import TrendsAnalysis from "./TrendsAnalysis";
import TrafficInsight from "./TrafficInsight";

const ALL_TABS = ["Daily", "Staff Performance", "Stations", "Insurance", "Patient Insights", "Journey Trace", "Service Reviews", "Expired Tickets", "Conversation Log", "Trends & Analysis"];
// Tabs keep their numbers (0-9). A brand without client messaging drops Conversation Log (8);
// "Trends & Analysis" (9) only exists in the redesigned (modern) builds and sits after Daily.
const MODERN_REPORTS = BRAND.reportsRedesign;
// In the redesigned builds the Insurance tab (3) becomes "Categories & Traffic".
const TAB_DEFS = ALL_TABS.map((label, id) => ({ label: MODERN_REPORTS && id === 3 ? "Categories & Traffic" : label, id }))
  .filter((t) => (t.id !== 8 || BRAND.messaging) && (t.id !== 9 || MODERN_REPORTS));
if (MODERN_REPORTS) TAB_DEFS.splice(1, 0, TAB_DEFS.splice(TAB_DEFS.findIndex((t) => t.id === 9), 1)[0]);

export default function ReportsPage() {
  const [tab,       setTab]      = useState(0);
  const [date,      setDate]     = useState(today());
  const [rangeFrom, setRangeFrom]= useState(daysAgo(30));
  const [search,    setSearch]   = useState("");
  const [loading,   setLoading]  = useState(false);
  const [data,      setData]     = useState(null);

  const load = async () => {
    // The redesigned Daily tab loads its own data (and keeps its filters across date changes)
    if ((tab === 0 && MODERN_REPORTS) || (tab === 3 && MODERN_REPORTS) || tab === 9) { setLoading(false); setData(null); return; }
    setLoading(true);
    setData(null);
    try {
      let res;
      if (tab === 0) res = await facilityAdmin.dailyReport({ date });
      if (tab === 1) res = await facilityAdmin.staffReport({ date_from: date, date_to: date });
      if (tab === 2) res = await facilityAdmin.stationReport({ date_from: date, date_to: date });
      if (tab === 3) res = await facilityAdmin.insuranceReport({ date_from: rangeFrom, date_to: date });
      if (tab === 4) res = await facilityAdmin.patientInsights({ date_from: date, date_to: date });
      if (tab === 5) return; // Journey Trace has its own search UI
      if (tab === 6) res = await facilityAdmin.serviceReviewsReport({ date_from: date, date_to: date });
      if (tab === 7) res = await facilityAdmin.expiredTicketsReport({ date_from: date, date_to: date });
      if (tab === 8) res = await facilityAdmin.conversationLog({ date_from: date, date_to: date, search });
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Failed to load report.`);
      console.error(`[Reports] Tab ${tab} failed:`, msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab, date, rangeFrom, search]);

  return (
    <div style={styles.root}>
      <PageHeader title="Reports" subtitle="Queue performance analytics for your facility." />
      <div style={styles.toolbar}>
        <div style={styles.tabs}>
          {TAB_DEFS.map(({ label, id }) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ ...styles.tab, ...(tab === id ? styles.tabActive : {}) }}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {tab === 8 && (
            <input
              style={{ ...styles.datePicker, width: "200px" }}
              placeholder="Search ticket, patient, phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          )}
          {tab === 3 && !MODERN_REPORTS && (
            <>
              <input type="date" style={styles.datePicker}
                value={rangeFrom} onChange={e => setRangeFrom(e.target.value)} />
              <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>to</span>
            </>
          )}
          {tab !== 9 && !(tab === 3 && MODERN_REPORTS) && <input type="date" style={styles.datePicker}
            value={date} onChange={e => setDate(e.target.value)} />}
        </div>
      </div>
      {(tab === 6 || tab === 7 || tab === 8) && (
        <div style={{ fontSize: "11px", color: "var(--text-faint)", margin: "-14px 0 16px" }}>
          Showing data for {date} only — change the date above to view a different day.
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <>
          {tab === 0 && (MODERN_REPORTS ? <DailyOverview date={date} /> : data && <DailyView data={data} />)}
          {tab === 1 && data && <StaffView data={data} />}
          {tab === 2 && data && <StationView data={data} />}
          {tab === 3 && (MODERN_REPORTS ? <TrafficInsight /> : data && <InsuranceView data={data} />)}
          {tab === 4 && data && <PatientInsightsView data={data} />}
          {tab === 5 && <JourneyTraceView />}
          {tab === 6 && data && <ServiceReviewsView data={data} />}
          {tab === 7 && data && <ExpiredTicketsView data={data} />}
          {tab === 8 && data && <ConversationLogView data={data} />}
          {tab === 9 && <TrendsAnalysis />}
        </>
      )}
    </div>
  );
}

// ── Daily Report ──────────────────────────────────────────────────────────────
function DailyView({ data }) {
  const maxHour = Math.max(...Object.values(data.hourly_volume || {}).map(Number), 1);
  return (
    <div style={V.col}>
      <div style={V.statsRow}>
        <StatCard label="Total Tickets"  value={data.total_tickets}   color="#60A5FA" />
        <StatCard label="Completed"       value={data.completed}       color="#22C55E" />
        <StatCard label="Still in Queue"  value={data.still_in_queue}  color="#EAB308" />
        <StatCard label="Abandoned"       value={data.abandoned}       color="#EF4444" />
        <StatCard label="Expired"         value={data.expired ?? 0}    color="#F97316" />
        <StatCard label="Completion Rate" value={`${data.completion_rate ?? 0}%`} color="#A78BFA" />
        <StatCard label="Avg Wait (min)"  value={data.avg_wait_minutes ?? 0} color="#34D399" />
      </div>

      {/* Hourly volume */}
      <div style={V.card}>
        <div style={V.cardTitle}>Hourly Volume</div>
        <div style={V.barChart}>
          {Array.from({ length: 24 }, (_, h) => {
            const count = data.hourly_volume?.[h] || 0;
            return (
              <div key={h} style={V.barCol}>
                <div style={V.barLabel}>{count || ""}</div>
                <div style={{ ...V.bar, height: `${Math.round((count / maxHour) * 60)}px`, minHeight: count ? "4px" : "0" }} />
                <div style={V.barHour}>{h}h</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Station stats */}
      {(data.station_stats?.length > 0 || data.station_breakdown?.length > 0) && (
        <div style={V.card}>
          <div style={V.cardTitle}>Station Breakdown</div>
          <table style={V.table}>
            <thead><tr>
              {["Station", "Visits", "Served", "Avg Wait (min)", "Avg Service (min)"].map(h =>
                <th key={h} style={V.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {(data.station_stats || data.station_breakdown || []).map((s, i) => (
                <tr key={i} style={V.tr}>
                  <td style={V.td}>
                    <span style={{ color: "var(--text-primary)" }}>{s.station_name}</span>
                    <br /><span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{s.zone_name}</span>
                  </td>
                  <td style={{ ...V.td, textAlign: "center" }}>{s.total_visits ?? s.served ?? 0}</td>
                  <td style={{ ...V.td, textAlign: "center", color: "#22C55E" }}>{s.total_served ?? s.served ?? 0}</td>
                  <td style={{ ...V.td, textAlign: "center", color: (s.avg_wait_minutes || s.avg_wait_min || 0) > 15 ? "#EF4444" : "var(--text-secondary)" }}>
                    {s.avg_wait_minutes ?? s.avg_wait_min ?? 0} min
                  </td>
                  <td style={{ ...V.td, textAlign: "center" }}>
                    {s.avg_service_minutes ?? s.avg_service_min ?? 0} min
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Staff Report ──────────────────────────────────────────────────────────────
// Ranked by Efficiency Score — the same 0-100 formula (avg service speed +
// volume, weighted evenly) used on each staff member's own "My Performance"
// page, so this facility-wide "who's actually best overall" view and each
// individual's personal ranking never disagree. Raw ticket count alone
// (the old sort) rewards volume over speed, which isn't a fair "best".
function StaffView({ data }) {
  const staffList = data.staff || data.results || [];
  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Staff Performance</div>
        {staffList.length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "16px" }}>
            No staff activity in this period. Note: staff must be assigned to journeys
            for performance data to appear.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "4px" }}>
            {staffList.map((s, i) => (
              <div key={i} style={V.staffRow}>
                <div style={V.staffRank}>#{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "5px", flexWrap: "wrap", gap: "6px" }}>
                    <div>
                      <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" }}>{s.staff_name}</span>
                      <span style={{ fontSize: "11px", color: "var(--text-muted)", marginLeft: "8px" }}>{s.station_name}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "13px", color: "#60A5FA", fontWeight: "600" }}>
                        {s.total_served} served
                      </span>
                      <span style={{
                        fontSize: "12px", fontWeight: "700", padding: "2px 8px", borderRadius: "6px",
                        color: s.efficiency_score >= 70 ? "#22C55E" : s.efficiency_score >= 40 ? "#EAB308" : "#EF4444",
                        background: s.efficiency_score >= 70 ? "rgba(34,197,94,0.12)" : s.efficiency_score >= 40 ? "rgba(234,179,8,0.12)" : "rgba(239,68,68,0.12)",
                      }}>
                        {s.efficiency_score}% efficiency
                      </span>
                    </div>
                  </div>
                  <div style={V.progressBar}>
                    <div style={{ ...V.progressFill, width: `${s.efficiency_score}%` }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "4px", flexWrap: "wrap", gap: "6px" }}>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                      Avg service: {s.avg_service_minutes ?? 0} min
                    </div>
                    {s.tat_total > 0 ? (
                      <div style={{ fontSize: "11px", fontWeight: "600", color: s.tat_compliance_pct >= 80 ? "#22C55E" : s.tat_compliance_pct >= 50 ? "#EAB308" : "#EF4444" }}>
                        TAT: {s.tat_compliance_pct}% within {s.tat_target_minutes}m ({s.tat_within}/{s.tat_total})
                      </div>
                    ) : (
                      <div style={{ fontSize: "11px", color: "var(--text-faint)" }}>
                        {s.tat_target_minutes ? "No called tickets to measure" : "No TAT threshold set for this station"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Station Report ────────────────────────────────────────────────────────────
function StationView({ data }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (name) => setExpanded(p => ({ ...p, [name]: !p[name] }));
  const stations = data.stations || data.results || [];

  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Station Efficiency</div>
        {stations.length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "16px" }}>
            No station activity in this period.
          </div>
        ) : (
          <table style={V.table}>
            <thead><tr>
              {["Station", "Visits", "Served", "Expired", "Avg Wait", "Avg Service", "Bottleneck?"].map(h =>
                <th key={h} style={V.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {stations.map((s, i) => (
                <React.Fragment key={i}>
                  <tr
                    style={{ ...V.tr, cursor: s.counters?.length > 0 ? "pointer" : "default" }}
                    onClick={() => s.counters?.length > 0 && toggle(s.station_name)}
                  >
                    <td style={V.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {s.counters?.length > 0 && (
                          <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                            {expanded[s.station_name] ? "▾" : "▸"}
                          </span>
                        )}
                        <div>
                          <span style={{ color: "var(--text-primary)", fontSize: "14px" }}>{s.station_name}</span>
                          <br />
                          <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{s.zone_name}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...V.td, textAlign: "center" }}>{s.total_visits ?? 0}</td>
                    <td style={{ ...V.td, textAlign: "center", color: "#22C55E" }}>{s.served ?? 0}</td>
                    <td style={{ ...V.td, textAlign: "center", color: s.expired_at_station > 0 ? "#F97316" : "var(--text-faint)" }}>
                      {s.expired_at_station ?? 0}
                    </td>
                    <td style={{ ...V.td, textAlign: "center", color: s.avg_wait_minutes > 15 ? "#EF4444" : "var(--text-secondary)" }}>
                      {s.avg_wait_minutes ?? 0} min
                    </td>
                    <td style={{ ...V.td, textAlign: "center", color: "var(--text-secondary)" }}>
                      {s.avg_service_minutes ?? 0} min
                    </td>
                    <td style={{ ...V.td, textAlign: "center" }}>
                      {s.is_bottleneck ? (
                        <div>
                          <span style={{ color: "#EF4444", fontSize: "12px", fontWeight: "600" }}>⚠ Yes</span>
                          {s.bottleneck_reason && (
                            <div style={{ fontSize: "10px", color: "#EF4444", marginTop: "2px", maxWidth: "140px" }}>
                              {s.bottleneck_reason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "var(--text-faint)" }}>—</span>
                      )}
                    </td>
                  </tr>

                  {/* Counter breakdown (expandable) */}
                  {expanded[s.station_name] && s.counters?.map((c, ci) => (
                    <tr key={`${i}-${ci}`} style={{ ...V.tr, background: "rgba(37,99,235,0.04)" }}>
                      <td style={{ ...V.td, paddingLeft: "36px" }}>
                        <span style={{ fontSize: "12px", color: "#60A5FA" }}>
                          Counter {c.counter_number ?? ci + 1}: {c.counter_name}
                        </span>
                      </td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px" }}>{c.total_visits ?? 0}</td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px", color: "#22C55E" }}>{c.served ?? 0}</td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px" }}>—</td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px", color: c.avg_wait_minutes > 15 ? "#EF4444" : "var(--text-secondary)" }}>
                        {c.avg_wait_minutes ?? 0} min
                      </td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px", color: "var(--text-secondary)" }}>
                        {c.avg_service_minutes ?? 0} min
                      </td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px" }}>
                        {c.avg_wait_minutes > 15 ? (
                          <span style={{ color: "#EF4444", fontSize: "11px" }}>⚠ Slow</span>
                        ) : (
                          <span style={{ color: "var(--text-faint)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Insurance / Patient Category Report ─────────────────────────────────────
// How many Insured vs Not Insured (or whatever categories the facility has
// configured under Insurance / Prefixes) visited over a period, plus how
// that split moved day to day. Categories come from the report response
// itself (InsuranceRoutingRule-driven) rather than being hardcoded here, so
// a facility with 3+ categories, or different label names, renders exactly
// the same way with no frontend changes.
const PIE_COLORS = ["#60A5FA", "#F97316", "#22C55E", "#A78BFA", "#EAB308", "#EF4444", "#34D399", "#F472B6"];

function InsuranceView({ data }) {
  const breakdown = data.breakdown || [];
  const trend = data.daily_trend || [];
  const colorFor = (label) => PIE_COLORS[breakdown.findIndex(b => b.label === label) % PIE_COLORS.length];

  return (
    <div style={V.col}>
      <div style={V.statsRow}>
        <StatCard label="Total Tickets" value={data.total_tickets} color="#60A5FA" />
        {breakdown.map(b => (
          <StatCard key={b.label} label={b.prefix ? `${b.label} (${b.prefix})` : b.label} value={b.count} color={colorFor(b.label)} />
        ))}
      </div>

      <div style={V.card}>
        <div style={V.cardTitle}>Category Breakdown</div>
        {data.total_tickets === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "16px" }}>
            No tickets in this period.
          </div>
        ) : (
          <div style={{ display: "flex", gap: "32px", alignItems: "center", flexWrap: "wrap", padding: "8px 0" }}>
            <DonutChart breakdown={breakdown} total={data.total_tickets} colorFor={colorFor} />
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1, minWidth: "220px" }}>
              {breakdown.map(b => (
                <div key={b.label} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ width: "12px", height: "12px", borderRadius: "3px", background: colorFor(b.label), flexShrink: 0 }} />
                  <span style={{ fontSize: "13px", color: "var(--text-primary)", flex: 1 }}>
                    {b.label}{b.prefix && <span style={{ color: "var(--text-muted)" }}> ({b.prefix})</span>}
                  </span>
                  <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)" }}>{b.count}</span>
                  <span style={{ fontSize: "12px", color: "var(--text-muted)", width: "44px", textAlign: "right" }}>{b.percent}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {trend.length > 0 && (
        <div style={V.card}>
          <div style={V.cardTitle}>Trend Over Time</div>
          <div style={{ display: "flex", gap: "14px", marginBottom: "8px", flexWrap: "wrap" }}>
            {breakdown.filter(b => b.count > 0).map(b => (
              <div key={b.label} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "var(--text-secondary)" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: colorFor(b.label) }} />
                {b.label}
              </div>
            ))}
          </div>
          <TrendLineChart trend={trend} breakdown={breakdown} colorFor={colorFor} />
        </div>
      )}
    </div>
  );
}

// Animated donut — concentric SVG stroke arcs (one <circle> per category,
// rotated to its own start angle, stroke-dasharray driven by its share of
// the total) rather than a plain conic-gradient, so each segment can grow
// in on mount via a CSS transition. No charting library needed.
function DonutChart({ breakdown, total, colorFor }) {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    setGrown(false);
    const t = setTimeout(() => setGrown(true), 50);
    return () => clearTimeout(t);
  }, [breakdown]);

  const size = 180, stroke = 26, r = (size - stroke) / 2, circumference = 2 * Math.PI * r;
  let seenCount = 0;

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-border)" strokeWidth={stroke} />
        {breakdown.filter(b => b.count > 0).map((b, i) => {
          const frac    = total ? b.count / total : 0;
          const dashLen = frac * circumference;
          const rotate  = total ? (seenCount / total) * 360 : 0;
          seenCount += b.count;
          return (
            <circle
              key={b.label}
              cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={colorFor(b.label)} strokeWidth={stroke} strokeLinecap="butt"
              strokeDasharray={`${grown ? dashLen : 0} ${circumference}`}
              style={{
                transformOrigin: "50% 50%",
                transform: `rotate(${rotate}deg)`,
                transition: `stroke-dasharray 0.9s cubic-bezier(0.22,1,0.36,1) ${i * 120}ms`,
              }}
            />
          );
        })}
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", pointerEvents: "none",
      }}>
        <div style={{ fontSize: "26px", fontWeight: "800", color: "var(--text-primary)", lineHeight: 1 }}>{total}</div>
        <div style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: "600", letterSpacing: "0.06em", textTransform: "uppercase", marginTop: "3px" }}>Tickets</div>
      </div>
    </div>
  );
}

// Animated multi-series line chart — plain SVG, one <path> per category
// with a "draw-on" animation (classic stroke-dasharray/dashoffset trick:
// set dasharray to the path's own real length via getTotalLength(), start
// dashoffset at that same length so nothing is visible, then transition it
// to 0 so the line appears to draw itself). Points animate as circles that
// scale in with a per-point delay once the line reaches them.
function TrendLineChart({ trend, breakdown, colorFor }) {
  const pathRefs = React.useRef({});
  const W = 640, H = 200, padL = 32, padR = 12, padT = 12, padB = 24;
  const categories = breakdown.filter(b => b.count > 0).map(b => b.label);
  const maxVal = Math.max(
    ...trend.flatMap(d => categories.map(c => d.counts[c] || 0)),
    1
  );
  const n = trend.length;
  const xFor = (i) => n <= 1 ? padL : padL + (i / (n - 1)) * (W - padL - padR);
  const yFor = (v) => H - padB - (v / maxVal) * (H - padT - padB);

  const linePaths = categories.map(label => {
    const d = trend
      .map((day, i) => `${i === 0 ? "M" : "L"} ${xFor(i)},${yFor(day.counts[label] || 0)}`)
      .join(" ");
    return { label, d };
  });

  useEffect(() => {
    Object.entries(pathRefs.current).forEach(([label, el], i) => {
      if (!el) return;
      const length = el.getTotalLength();
      el.style.transition = "none";
      el.style.strokeDasharray = `${length}`;
      el.style.strokeDashoffset = `${length}`;
      el.getBoundingClientRect(); // force reflow so the transition below actually animates
      el.style.transition = `stroke-dashoffset 1.1s cubic-bezier(0.22,1,0.36,1) ${i * 150}ms`;
      el.style.strokeDashoffset = "0";
    });
  }, [trend]);

  // Thin out x-axis labels so they don't collide on wide date ranges.
  const labelEvery = Math.max(1, Math.ceil(n / 10));

  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ minWidth: `${Math.max(W, n * 24)}px`, display: "block" }}>
        {[0.25, 0.5, 0.75, 1].map(f => (
          <line key={f} x1={padL} x2={W - padR} y1={yFor(maxVal * f)} y2={yFor(maxVal * f)}
            stroke="var(--surface-border)" strokeWidth="1" strokeDasharray="3 3" />
        ))}
        {trend.map((day, i) => (
          i % labelEvery === 0 && (
            <text key={day.date} x={xFor(i)} y={H - 6} fontSize="9" fill="var(--text-faint)" textAnchor="middle">
              {day.date.slice(5)}
            </text>
          )
        ))}
        {linePaths.map(({ label, d }) => (
          <path
            key={label}
            ref={el => { pathRefs.current[label] = el; }}
            d={d} fill="none" stroke={colorFor(label)} strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round"
          />
        ))}
        {linePaths.map(({ label }) => trend.map((day, i) => (
          <circle
            key={`${label}-${day.date}`}
            cx={xFor(i)} cy={yFor(day.counts[label] || 0)} r="3"
            fill={colorFor(label)}
            style={{
              opacity: 0, animation: `reportsDotIn 0.4s ease-out ${0.9 + i * (1.1 / Math.max(n, 1))}s forwards`,
            }}
          />
        )))}
      </svg>
      <style>{`@keyframes reportsDotIn { from { opacity: 0; transform: scale(0.4); } to { opacity: 1; transform: scale(1); } }`}</style>
    </div>
  );
}

function PatientInsightsView({ data }) {
  const [showFull, setShowFull] = React.useState({});
  const patients = data?.patients || [];
  const toggle = (i) => setShowFull(p => ({ ...p, [i]: !p[i] }));

  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Frequent Visitors — {patients.length} patients found</div>
        {patients.length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "16px" }}>
            No patient data for this period.
          </div>
        ) : (
          <table style={V.table}>
            <thead><tr>
              {["Patient", "Phone", "Visits", "Last Visit", "Avg Wait (min)", "Stations Visited"].map(h =>
                <th key={h} style={V.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {patients.map((p, i) => (
                <tr key={i} style={V.tr}>
                  <td style={{ ...V.td, color: "var(--text-primary)", fontWeight: "500" }}>{p.patient_name}</td>
                  <td style={V.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontSize: "12px" }}>
                        {showFull[i] ? p.phone : p.phone_masked}
                      </span>
                      <button onClick={() => toggle(i)}
                        style={{ background: "none", border: "none", color: "#2563EB", cursor: "pointer", fontSize: "11px", padding: "2px 6px", fontFamily: "inherit" }}>
                        {showFull[i] ? "Hide" : "Show"}
                      </button>
                    </div>
                  </td>
                  <td style={{ ...V.td, textAlign: "center" }}>
                    <span style={{ color: p.visit_count >= 5 ? "#EF4444" : p.visit_count >= 3 ? "#EAB308" : "#22C55E", fontWeight: "700" }}>
                      {p.visit_count}
                    </span>
                  </td>
                  <td style={{ ...V.td, color: "var(--text-secondary)", fontSize: "12px" }}>{p.last_visit || "—"}</td>
                  <td style={{ ...V.td, textAlign: "center", color: p.avg_wait_minutes > 15 ? "#EF4444" : "var(--text-secondary)" }}>
                    {p.avg_wait_minutes ?? 0} min
                  </td>
                  <td style={{ ...V.td, fontSize: "11px", color: "var(--text-secondary)" }}>
                    {(p.stations_visited || []).join(" → ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: "11px", color: "var(--text-faint)", marginTop: "14px", padding: "8px", background: "rgba(37,99,235,0.04)", borderRadius: "6px" }}>
          🔒 Phone numbers are masked by default. Click "Show" to reveal full number.
          Only facility admins can view full phone numbers.
        </div>
      </div>
    </div>
  );
}

function JourneyTraceView() {
  const [search, setSearch]         = React.useState({ type: "ticket", ticket: "", phone: "", date: today() });
  const [result, setResult]         = React.useState(null);
  const [loading, setLoading]       = React.useState(false);
  const [error,   setError]         = React.useState(null);
  const [expanded, setExpanded]     = React.useState(null);
  const [convoStep, setConvoStep]   = React.useState(null);

  const handleSearch = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const params = search.type === "ticket"
        ? { ticket_number: search.ticket, date: search.date }
        : { phone: search.phone };
      const res = await facilityAdmin.journeyTrace(params);
      setResult(res.data);
      if (res.data.traces?.length === 1) setExpanded(0);
    } catch (e) {
      setError(e.response?.data?.error || "Not found.");
    } finally { setLoading(false); }
  };

  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Patient Journey Trace</div>
        <div style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "16px" }}>
          Look up a patient's full journey through the facility — every station, wait time, staff member, and outcome.
        </div>

        <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: "0" }}>
            {["ticket", "phone"].map(t => (
              <button key={t} onClick={() => setSearch(p => ({ ...p, type: t }))}
                style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "600", fontFamily: "inherit", cursor: "pointer", border: "1px solid rgba(37,99,235,0.3)", background: search.type === t ? "rgba(37,99,235,0.2)" : "var(--input-bg)", color: search.type === t ? "#60A5FA" : "var(--text-secondary)", borderRadius: t === "ticket" ? "8px 0 0 8px" : "0 8px 8px 0" }}>
                {t === "ticket" ? "🎫 Ticket Number" : "📱 Phone Number"}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {search.type === "ticket" ? (
            <>
              <input
                style={{ ...styles.datePicker, flex: 1, minWidth: "150px" }}
                placeholder="Ticket number (e.g. A-001)"
                value={search.ticket}
                onChange={e => setSearch(p => ({ ...p, ticket: e.target.value.toUpperCase() }))}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
              />
              <input type="date" style={styles.datePicker}
                value={search.date}
                onChange={e => setSearch(p => ({ ...p, date: e.target.value }))} />
            </>
          ) : (
            <input
              style={{ ...styles.datePicker, flex: 1 }}
              placeholder="Phone number (e.g. +233201234567)"
              value={search.phone}
              onChange={e => setSearch(p => ({ ...p, phone: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
            />
          )}
          <button onClick={handleSearch} disabled={loading}
            style={{ background: "linear-gradient(135deg,#2563EB,#1D4ED8)", border: "none", borderRadius: "8px", color: "#fff", fontSize: "13px", fontWeight: "600", padding: "8px 20px", cursor: "pointer", fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Searching…" : "Search →"}
          </button>
        </div>

        <div style={{ fontSize: "11px", color: "var(--text-faint)", marginTop: "8px" }}>
          ⚠ Ticket numbers reset daily. Always include the date when searching by ticket number.
        </div>
      </div>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "10px", padding: "14px 18px", color: "#EF4444", fontSize: "13px" }}>
          {error}
        </div>
      )}

      {result?.traces?.map((trace, ti) => (
        <div key={ti} style={V.card}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "22px", fontWeight: "800", color: "#60A5FA", fontVariantNumeric: "tabular-nums" }}>{trace.ticket_number}</span>
                <span style={{ fontSize: "11px", padding: "3px 8px", borderRadius: "6px", background: trace.status === "completed" ? "rgba(34,197,94,0.1)" : "rgba(234,179,8,0.1)", color: trace.status === "completed" ? "#22C55E" : "#EAB308", fontWeight: "600", textTransform: "uppercase" }}>{trace.status}</span>
                {trace.priority !== "normal" && <span style={{ color: trace.priority === "emergency" ? "#EF4444" : "#EAB308", fontSize: "14px" }}>{trace.priority === "emergency" ? "🚨" : "⚡"}</span>}
              </div>
              <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: "500", marginTop: "4px" }}>{trace.patient_name}</div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                {trace.date} · Issued: {trace.issued_at} · {trace.reason_for_visit}
                {trace.insurance_type && trace.insurance_type !== "—" && ` · ${trace.insurance_type}`}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "13px", color: "var(--text-primary)" }}>
                {trace.total_stations} station{trace.total_stations !== 1 ? "s" : ""} · {trace.total_minutes != null ? `${trace.total_minutes} min total` : "In progress"}
              </div>
              {trace.bottleneck_station && (
                <div style={{ fontSize: "11px", color: "#EF4444", marginTop: "3px" }}>
                  ⚠ Longest wait: {trace.bottleneck_station} ({trace.max_wait_minutes} min)
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {trace.steps.map((step, si) => (
              <div key={si} style={{ display: "flex", gap: "12px", alignItems: "flex-start", padding: "12px 14px", borderRadius: "10px", background: step.is_bottleneck ? "rgba(239,68,68,0.06)" : "var(--input-bg)", border: `1px solid ${step.is_bottleneck ? "rgba(239,68,68,0.2)" : "rgba(37,99,235,0.08)"}` }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: step.status === "completed" || step.status === "transferred" ? "rgba(34,197,94,0.15)" : step.status === "skipped" ? "rgba(239,68,68,0.15)" : step.status === "held" ? "rgba(245,158,11,0.18)" : "var(--surface-border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", flexShrink: 0 }}>
                  {step.status === "completed" || step.status === "transferred" ? "✓" : step.status === "skipped" ? "✗" : step.status === "held" ? "⏸" : `${si + 1}`}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" }}>{step.station}</span>
                    <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{step.zone}</span>
                    {step.is_bottleneck && <span style={{ fontSize: "11px", color: "#EF4444", fontWeight: "700" }}>⚠ BOTTLENECK</span>}
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    <span>Arrived: <span style={{ color: "var(--text-secondary)" }}>{step.arrived_at || "—"}</span></span>
                    <span>Called: <span style={{ color: "var(--text-secondary)" }}>{step.called_at || "—"}</span></span>
                    <span>Done: <span style={{ color: "var(--text-secondary)" }}>{step.completed_at || "—"}</span></span>
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "4px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    {step.wait_minutes != null && <span>Wait: <span style={{ color: step.wait_minutes > 15 ? "#EF4444" : "#34D399", fontWeight: "600" }}>{step.wait_minutes} min</span></span>}
                    {step.service_minutes != null && <span>Service: <span style={{ color: "#60A5FA", fontWeight: "600" }}>{step.service_minutes} min</span></span>}
                    {step.staff !== "—" && <span>Staff: <span style={{ color: "var(--text-secondary)" }}>{step.staff}</span></span>}
                    {step.counter !== "—" && <span>Counter: <span style={{ color: "var(--text-secondary)" }}>{step.counter}</span></span>}
                    {step.outcome && <span>Outcome: <span style={{ color: "#A78BFA" }}>{step.outcome}</span></span>}
                    {step.notes && <span style={{ color: "#EF4444", fontStyle: "italic" }}>{step.notes}</span>}
                  </div>
                </div>
                {BRAND.messaging && step.messages?.length > 0 && (
                  <button
                    onClick={() => setConvoStep(step)}
                    title="View conversation at this station"
                    style={{
                      flexShrink: 0, background: "rgba(37,99,235,0.1)", border: "1px solid rgba(37,99,235,0.25)",
                      borderRadius: "8px", color: "#60A5FA", fontSize: "11px", fontWeight: "600",
                      padding: "6px 10px", cursor: "pointer", fontFamily: "inherit",
                      display: "flex", alignItems: "center", gap: "4px",
                    }}
                  >
                    💬 {step.messages.length}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <Modal open={!!convoStep} onClose={() => setConvoStep(null)} title={convoStep ? `Conversation — ${convoStep.station}` : ""} width="460px">
        {convoStep && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "60vh", overflowY: "auto" }}>
            {convoStep.messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.from === "patient" ? "flex-start" : "flex-end",
                maxWidth: "85%", padding: "8px 12px", borderRadius: "10px",
                fontSize: "13px", lineHeight: 1.4,
                background: m.from === "patient" ? "rgba(52,211,153,0.12)" : "rgba(37,99,235,0.15)",
                border: `1px solid ${m.from === "patient" ? "rgba(52,211,153,0.3)" : "rgba(37,99,235,0.3)"}`,
                color: "var(--text-primary)",
              }}>
                <div style={{ fontSize: "10px", fontWeight: "700", opacity: 0.7, marginBottom: "2px", textTransform: "uppercase" }}>
                  {m.from === "patient" ? "Patient" : (m.sent_by_name || "Staff")} · {m.sent_at}
                </div>
                {m.text}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Service Reviews (patient ratings) ───────────────────────────────────────
function ServiceReviewsView({ data }) {
  const fo = data.facility_overall || {};
  return (
    <div style={V.col}>
      {/* Facility-wide "how was your overall visit" rating — separate
          from the per-staff ratings below. Belongs to the facility, not
          any one staff member: submitted once per ticket at completion. */}
      <div style={V.card}>
        <div style={V.cardTitle}>Overall Facility Rating</div>
        <div style={{ display: "flex", alignItems: "center", gap: "24px", padding: "8px 0" }}>
          <div>
            <div style={{ fontSize: "36px", fontWeight: "800", color: "#EAB308", lineHeight: 1 }}>
              {fo.count ? `⭐ ${fo.avg_stars}` : "—"}
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>
              {fo.count || 0} whole-visit rating{fo.count === 1 ? "" : "s"} in this period
            </div>
          </div>
          {fo.count > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
              {[5, 4, 3, 2, 1].map(n => {
                const count = fo.distribution?.[String(n)] || 0;
                const pct = fo.count ? Math.round((count / fo.count) * 100) : 0;
                return (
                  <div key={n} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ width: "28px", fontSize: "11px", color: "var(--text-secondary)" }}>{n}⭐</div>
                    <div style={{ flex: 1, height: "6px", borderRadius: "3px", background: "var(--surface-border)", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "#EAB308" }} />
                    </div>
                    <div style={{ width: "20px", fontSize: "11px", color: "var(--text-muted)", textAlign: "right" }}>{count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {(fo.recent_comments || []).length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--surface-border)" }}>
            {fo.recent_comments.slice(0, 5).map((c, i) => (
              <div key={i} style={{ fontSize: "12px", color: "var(--text-secondary)", fontStyle: "italic" }}>
                "{c.comment}" <span style={{ color: "var(--text-muted)", fontStyle: "normal" }}>— {c.ticket_number}, {"⭐".repeat(c.stars)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={V.statsRow}>
        <StatCard label="Staff Ratings Total" value={data.total_ratings}                         color="#60A5FA" />
        <StatCard label="Staff Avg"           value={data.total_ratings ? `⭐ ${data.overall_avg_stars}` : "—"} color="#EAB308" />
      </div>

      <div style={V.card}>
        <div style={V.cardTitle}>Per-Station Rating Distribution</div>
        {data.total_ratings === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "16px" }}>No ratings for this day.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
            {[5, 4, 3, 2, 1].map(n => {
              const count = data.distribution?.[String(n)] || 0;
              const pct = data.total_ratings ? Math.round((count / data.total_ratings) * 100) : 0;
              return (
                <div key={n} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "36px", fontSize: "12px", color: "var(--text-secondary)" }}>{n} ⭐</div>
                  <div style={{ flex: 1, height: "8px", borderRadius: "4px", background: "var(--surface-border)", overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#EAB308" }} />
                  </div>
                  <div style={{ width: "30px", fontSize: "12px", color: "var(--text-muted)", textAlign: "right" }}>{count}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={V.card}>
        <div style={V.cardTitle}>Per-Staff Rating</div>
        {(data.staff_breakdown || []).length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "16px" }}>No staff-attributed ratings yet.</div>
        ) : (
          <table style={V.table}>
            <thead><tr>{["Staff", "Avg Rating", "Count"].map(h => <th key={h} style={V.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.staff_breakdown.map((s, i) => (
                <tr key={i} style={V.tr}>
                  <td style={V.td}>{s.staff_name}</td>
                  <td style={{ ...V.td, textAlign: "center", color: "#EAB308" }}>⭐ {s.avg_stars}</td>
                  <td style={{ ...V.td, textAlign: "center" }}>{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(data.recent_comments || []).length > 0 && (
        <div style={V.card}>
          <div style={V.cardTitle}>Recent Comments</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "8px" }}>
            {data.recent_comments.map((c, i) => (
              <div key={i} style={{ padding: "10px 14px", borderRadius: "10px", background: "var(--input-bg)", border: "1px solid var(--surface-border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-primary)" }}>{c.ticket_number} · {c.staff_name || "—"}</span>
                  <span style={{ fontSize: "12px", color: "#EAB308" }}>{"⭐".repeat(c.stars)}</span>
                </div>
                <div style={{ fontSize: "13px", color: "var(--text-secondary)", fontStyle: "italic" }}>"{c.comment}"</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Expired Tickets ("where it died") ───────────────────────────────────────
function ExpiredTicketsView({ data }) {
  return (
    <div style={V.col}>
      <div style={V.statsRow}>
        <StatCard label="Total Auto-Expired" value={data.total_expired} color="#F97316" />
      </div>
      <div style={{ fontSize: "12px", color: "var(--text-muted)", margin: "-8px 0 4px" }}>
        Tickets force-expired by the midnight reset — never manually completed or abandoned by staff.
      </div>

      <div style={V.card}>
        <div style={V.cardTitle}>Where Tickets Died — by Station</div>
        {(data.station_breakdown || []).length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "16px" }}>No expired tickets for this day.</div>
        ) : (
          <table style={V.table}>
            <thead><tr>{["Station", "Expired Count"].map(h => <th key={h} style={V.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.station_breakdown.map((s, i) => (
                <tr key={i} style={V.tr}>
                  <td style={V.td}>{s.station_name}</td>
                  <td style={{ ...V.td, textAlign: "center", color: "#F97316", fontWeight: "600" }}>{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(data.tickets || []).length > 0 && (
        <div style={V.card}>
          <div style={V.cardTitle}>Individual Expired Tickets</div>
          <table style={V.table}>
            <thead><tr>{["Ticket", "Patient", "Died At", "Priority", "Waited Before Expiry"].map(h => <th key={h} style={V.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.tickets.map((t, i) => (
                <tr key={i} style={V.tr}>
                  <td style={V.td}>{t.ticket_number}</td>
                  <td style={V.td}>{t.patient_name}</td>
                  <td style={V.td}>{t.died_at_station}</td>
                  <td style={{ ...V.td, textAlign: "center", color: t.priority === "emergency" ? "#EF4444" : t.priority === "priority" ? "#EAB308" : "var(--text-secondary)" }}>
                    {t.priority}
                  </td>
                  <td style={{ ...V.td, textAlign: "center" }}>
                    {t.wait_minutes_before_expiry != null ? `${t.wait_minutes_before_expiry} min` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Conversation Log (audit) ─────────────────────────────────────────────────
function ConversationLogView({ data }) {
  return (
    <div style={V.col}>
      <div style={V.statsRow}>
        <StatCard label="Messages This Day" value={data.count} color="#60A5FA" />
      </div>
      <div style={V.card}>
        <div style={V.cardTitle}>Full Message Audit Trail</div>
        {(data.entries || []).length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "13px", padding: "16px" }}>No messages for this day.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px", maxHeight: "70vh", overflowY: "auto" }}>
            {data.entries.map((e, i) => (
              <div key={i} style={{
                display: "flex", gap: "12px", padding: "10px 14px", borderRadius: "10px",
                background: "var(--input-bg)",
                borderLeft: `3px solid ${e.direction === "staff" ? "#2563EB" : "#22C55E"}`,
              }}>
                <div style={{ width: "70px", flexShrink: 0, fontSize: "11px", color: "var(--text-muted)" }}>
                  {new Date(e.sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "12px", fontWeight: "600", color: e.direction === "staff" ? "#60A5FA" : "#34D399", marginBottom: "2px" }}>
                    {e.ticket_number} · {e.patient_name} · {e.direction === "staff" ? `Staff${e.sent_by ? ` (${e.sent_by})` : ""}` : "Patient"}
                  </div>
                  <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>{e.text}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function today() {
  return new Date().toISOString().split("T")[0];
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

const styles = {
  root:       { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1100px" },
  toolbar:    { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" },
  tabs:       { display: "flex", gap: "4px" },
  tab:        { background: "none", border: "none", borderBottom: "2px solid transparent", padding: "8px 16px", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit" },
  tabActive:  { color: "#60A5FA", borderBottomColor: "#2563EB" },
  datePicker: { background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "8px", padding: "8px 12px", color: "var(--text-primary)", fontSize: "13px", outline: "none", fontFamily: "inherit" },
};

const V = {
  col:       { display: "flex", flexDirection: "column", gap: "16px" },
  statsRow:  { display: "flex", gap: "12px", flexWrap: "wrap" },
  card:      { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "12px", padding: "18px 20px" },
  cardTitle: { fontSize: "13px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "14px" },
  barChart:  { display: "flex", alignItems: "flex-end", gap: "3px", height: "100px", paddingTop: "20px" },
  barCol:    { flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: "2px", height: "100%" },
  bar:       { width: "100%", background: "rgba(37,99,235,0.5)", borderRadius: "3px 3px 0 0", transition: "height 0.3s" },
  barLabel:  { fontSize: "9px", color: "var(--text-faint)", minHeight: "12px" },
  barHour:   { fontSize: "9px", color: "var(--text-faint)", marginTop: "2px" },
  table:     { width: "100%", borderCollapse: "collapse" },
  th:        { padding: "9px 12px", fontSize: "11px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid var(--surface-border)" },
  tr:        { borderBottom: "1px solid rgba(37,99,235,0.05)" },
  td:        { padding: "11px 12px", fontSize: "13px", color: "var(--text-secondary)", verticalAlign: "middle" },
  staffRow:  { display: "flex", gap: "12px", alignItems: "flex-start" },
  staffRank: { width: "24px", height: "24px", borderRadius: "6px", background: "var(--surface-border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "700", color: "#60A5FA", flexShrink: 0, marginTop: "2px" },
  progressBar:  { height: "5px", background: "var(--input-border)", borderRadius: "3px", overflow: "hidden" },
  progressFill: { height: "100%", background: "linear-gradient(90deg,#2563EB,#60A5FA)", borderRadius: "3px", transition: "width 0.4s" },
};
